import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Subscription, filter, interval, merge, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DataService } from './Data Service/data.service';

const STORAGE_KEY = 'vendorMessagesLastSeenMaxId';
const POLL_MS = 20000;

/**
 * Tracks whether the vendor has unread procurement messages (company notes) vs. last time
 * they opened the Messages page. Drives sidebar pulse until viewed.
 */
@Injectable({ providedIn: 'root' })
export class VendorMessagesPulseService implements OnDestroy {
  readonly hasUnread$ = new BehaviorSubject<boolean>(false);

  private pollSub: Subscription | null = null;
  private routerSub: Subscription | null = null;

  constructor(
    private readonly dataService: DataService,
    private readonly router: Router
  ) {}

  /** Start polling and refresh on navigation (vendor sessions only). */
  startMonitoring(): void {
    this.stopMonitoring();
    this.refreshUnreadState();

    this.pollSub = merge(of(0), interval(POLL_MS))
      .pipe(switchMap(() => this.dataService.getMyVendorMessages()))
      .subscribe({
        next: (rows) => this.applyRows(rows),
        error: () => this.hasUnread$.next(false),
      });

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.refreshUnreadState());
  }

  stopMonitoring(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.routerSub?.unsubscribe();
    this.routerSub = null;
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
  }

  /** Re-fetch once and update pulse state. */
  refreshUnreadState(): void {
    this.dataService.getMyVendorMessages().subscribe({
      next: (rows) => this.applyRows(rows),
      error: () => this.hasUnread$.next(false),
    });
  }

  /**
   * Call after the vendor has opened the Messages page and loaded the list so the pulse stops
   * until a newer note appears.
   */
  markViewedForRows(rows: { id: number }[]): void {
    const maxId = rows.length === 0 ? 0 : Math.max(...rows.map((r) => r.id));
    const prev = this.readStoredMax();
    if (maxId >= prev) {
      localStorage.setItem(STORAGE_KEY, String(maxId));
    }
    this.hasUnread$.next(false);
  }

  private applyRows(rows: unknown): void {
    const list = Array.isArray(rows) ? rows : [];
    const maxId = list.length === 0 ? 0 : Math.max(...list.map((r: { id: number }) => r.id));
    const seen = this.readStoredMax();
    this.hasUnread$.next(maxId > seen);
  }

  private readStoredMax(): number {
    if (typeof localStorage === 'undefined') {
      return 0;
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw != null ? Number(raw) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }
}
